import type { Work } from '~/composables/api/useWorkApi'

// X (formerly Twitter) brand logo — official SVG path scaled to 24x24 viewBox.
// 共有作品プレイの各シェル（WorkPlay / PlayDialog / PlayImmersiveHeader）で使い回す。
export const X_LOGO_ICON
  = 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.07l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'

/**
 * 作品プレイ画面（Public Work の `WorkPlay`／アプリ内 `PlayDialog`）で共通の
 * Work アクション（いいね・X 共有・リンクコピー）ロジック。#443 観点A で 2 シェルの
 * 重複実装を解消するために抽出した。UI（ボタンの見た目）は各シェルが持ち、ロジックのみ共有する。
 *
 * - `shareSlug` / `shareTitle` は getter で受ける（親 props の変化に追従するため）。
 * - #751: いいねはログイン必須（#697 の匿名いいねを撤回）。未ログインでいいねを押したら
 *   全プレイシェル共通でサインイン CTA（useSignInCta）を開く。
 */
export function usePlayActions(opts: {
  shareSlug: () => string | undefined
  shareTitle: () => string | undefined
}) {
  const runtimeConfig = useRuntimeConfig()
  const { getWork, getPublicWork, likeWork, unlikeWork } = useApi()
  const { isAuthenticated } = useAuthInfo()
  const { trackWorkLike } = useFunnelEvent()
  const { open: openSignInCta } = useSignInCta()

  const isLiked = ref(false)
  const displayLikes = ref(0)
  const likeLoading = ref(false)
  const linkCopied = ref(false)

  const canShare = computed(() => !!opts.shareSlug())
  const shareUrl = computed(() => `${runtimeConfig.public.serviceDomain}/works/${opts.shareSlug() ?? ''}`)
  const shareText = computed(() => {
    const title = opts.shareTitle() ?? ''
    return `『${title}』を Code & Magic で遊ぼう！\n#CodeAndMagic #Pyxel\n👇\n`
  })

  /**
   * いいね状態（liked / likes）を取得して反映する。認証時は getWork、未認証時は
   * public エンドポイント経由（#697）。取得できた Work を返す（呼び出し側で decode
   * 状態などに再利用するため）。
   */
  const fetchLikeState = async (): Promise<Work | null> => {
    const slug = opts.shareSlug()
    if (!slug) return null
    try {
      const work = isAuthenticated ? await getWork(slug) : await getPublicWork(slug)
      isLiked.value = work.liked
      displayLikes.value = work.likes
      return work
    }
    catch {
      return null // non-fatal
    }
  }

  const onLikeClick = async () => {
    const slug = opts.shareSlug()
    if (!slug) return
    // #751: 未ログインはいいねせずサインイン CTA を開く。
    if (!isAuthenticated) {
      openSignInCta('like')
      return
    }
    if (likeLoading.value) return

    const wasLiked = isLiked.value
    isLiked.value = !wasLiked
    displayLikes.value += wasLiked ? -1 : 1
    likeLoading.value = true

    try {
      const res = wasLiked ? await unlikeWork(slug) : await likeWork(slug)
      displayLikes.value = res.likes
      isLiked.value = res.liked
      if (!wasLiked) trackWorkLike(slug)
    }
    catch {
      isLiked.value = wasLiked
      displayLikes.value += wasLiked ? 1 : -1
    }
    finally {
      likeLoading.value = false
    }
  }

  const shareOnX = (): void => {
    const params = new URLSearchParams({ text: shareText.value, url: shareUrl.value })
    window.open(`https://x.com/intent/tweet?${params.toString()}`, '_blank')
  }

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shareUrl.value)
    }
    catch {
      // Fallback for non-secure contexts (e.g. local IP)
      const textarea = document.createElement('textarea')
      textarea.value = shareUrl.value
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    linkCopied.value = true
    setTimeout(() => {
      linkCopied.value = false
    }, 2500)
  }

  return {
    isLiked,
    displayLikes,
    likeLoading,
    linkCopied,
    canShare,
    fetchLikeState,
    onLikeClick,
    shareOnX,
    copyLink,
  }
}
